import { useState } from 'react';
import { CLASSE_SURVOL, useListeClavier } from '../hooks/useListeClavier';
import { useRessource } from '../hooks/useRessource';
import { api } from '../lib/apiClient';
import { Badge, Champ } from './Elements';
import type { Etablissement } from '../types';

/** Une entree de la liste : un etablissement du referentiel, ou le texte tape a AJOUTER. */
interface Candidat {
  cle: string;
  nom: string;
  detail: string;
  nouveau: boolean;
  valide: boolean;
}

/**
 * « ECOLE FREQUENTEE » ASSISTEE PAR LE REFERENTIEL (2026-10-13), avec
 * l'ergonomie du choix d'une activite : on tape, la liste se filtre, on
 * choisit a la souris ou au clavier, et la valeur retenue s'affiche en
 * pastille avec « Retirer » et « Changer ». Un texte que le referentiel
 * ne connait pas se propose en derniere ligne, « Ajouter » : il entrera
 * dans le referentiel a l'enregistrement, NON VALIDE, pour que la
 * Direction le relise — c'est le serveur qui le fait.
 *
 * La valeur est le NOM, pas un identifiant : c'est ce que la fiche
 * stocke et affiche, et c'est ce que le serveur resout.
 */
export function ChampEtablissement({
  valeur,
  onChanger,
  valide,
}: {
  valeur: string;
  onChanger: (valeur: string) => void;
  /** L'etat de validation de l'etablissement lie a la fiche, pour le dire sur la pastille. */
  valide?: boolean | null;
}) {
  const [recherche, setRecherche] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const etablissements = useRessource(() => api.get<Etablissement[]>('/api/etablissements'), []);
  const tous = etablissements.donnees ?? [];

  const detailDe = (e: Etablissement) =>
    [e.arrondissement, e.statut === 'prive' ? 'privé' : e.statut === 'public' ? 'public' : null]
      .filter(Boolean)
      .join(' · ');

  const connu = tous.find((e) => e.nom.toLowerCase() === valeur.trim().toLowerCase()) ?? null;
  const terme = recherche.trim().toLowerCase();
  const candidats: Candidat[] = tous
    .filter((e) => !terme || `${e.nom} ${e.arrondissement ?? ''}`.toLowerCase().includes(terme))
    .slice(0, 12)
    .map((e) => ({ cle: e.id, nom: e.nom, detail: detailDe(e), nouveau: false, valide: e.valide }));
  // Le texte tape, s'il ne correspond a aucun nom : a ajouter.
  if (terme && !tous.some((e) => e.nom.toLowerCase() === terme)) {
    candidats.push({
      cle: 'nouveau',
      nom: recherche.trim(),
      detail: 'inconnu du référentiel — entrera à valider',
      nouveau: true,
      valide: false,
    });
  }

  const choisir = (candidat: Candidat) => {
    onChanger(candidat.nom);
    setRecherche('');
    setOuvert(false);
  };
  const clavier = useListeClavier(candidats, choisir);

  if (valeur.trim() && !ouvert) {
    const aValider = connu ? !connu.valide : true;
    return (
      <Champ libelle="École fréquentée">
        <div className="flex items-center gap-2">
          <span className="champ flex flex-1 items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate">{valeur}</span>
              {connu && connu.arrondissement && (
                <span className="shrink-0 text-xs text-encre-500">{detailDe(connu)}</span>
              )}
              {(aValider || valide === false) && (
                <Badge ton="alerte">{connu ? 'à valider' : 'nouveau, à valider'}</Badge>
              )}
            </span>
            <button
              type="button"
              className="shrink-0 text-sm text-encre-500 underline"
              onClick={() => onChanger('')}
            >
              Retirer
            </button>
          </span>
          <button
            type="button"
            className="shrink-0 text-sm text-encre-500 underline"
            onClick={() => {
              setRecherche('');
              setOuvert(true);
            }}
          >
            Changer
          </button>
        </div>
      </Champ>
    );
  }

  return (
    <Champ
      libelle="École fréquentée"
      aide="Tapez pour filtrer le référentiel ; un établissement absent se propose en fin de liste, à ajouter."
    >
      <div className="relative">
        <input
          className="champ"
          value={recherche}
          placeholder="Taper une école, un collège…"
          autoComplete="off"
          onChange={(evenement) => {
            setRecherche(evenement.target.value);
            setOuvert(true);
          }}
          onFocus={() => setOuvert(true)}
          onKeyDown={clavier.auClavier}
          // Le clic sur une option doit preceder la fermeture, sinon il
          // ne l'atteint jamais.
          onBlur={() => window.setTimeout(() => setOuvert(false), 150)}
        />

        {ouvert && (
          <ul
            ref={clavier.liste}
            className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-encre-200 bg-white shadow-lg"
          >
            {candidats.length === 0 ? (
              <li className="px-3 py-2 text-sm text-encre-400">Tapez le nom de l’établissement.</li>
            ) : (
              candidats.map((candidat, rang) => (
                <li key={candidat.cle} data-index={rang}>
                  <button
                    type="button"
                    className={`flex w-full items-baseline gap-2 px-3 py-2 text-left text-sm ${
                      rang === clavier.index ? CLASSE_SURVOL : ''
                    } ${candidat.nouveau ? 'border-t border-encre-100 text-amber-800' : ''}`}
                    onMouseEnter={() => clavier.survoler(rang)}
                    onClick={() => choisir(candidat)}
                  >
                    <span>{candidat.nouveau ? `Ajouter « ${candidat.nom} »` : candidat.nom}</span>
                    <span className="text-xs text-encre-500">{candidat.detail}</span>
                    {!candidat.nouveau && !candidat.valide && (
                      <span className="text-xs text-amber-700">à valider</span>
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </Champ>
  );
}
